import { useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import DatePicker from 'react-datepicker';

import Button from '../../../components/Button';
import Icon from '../../../components/Icon';
import NamedItem from '../../../components/NamedItem';
import downloadFile from '../../../utils/downloadFile';
import formatDate from '../../../utils/formatDate';
import {
  CsvReport,
  downloadReportCsv,
} from '../../../services/reportServices';
import { useNotification } from '../../../context/notificationContext';

export type ReportDescriptor = {
  // Names the report as the service knows it (and keys the card list).
  key: CsvReport;
  title: string;
  description: JSX.Element;
  hasDates?: boolean;
  fileName: string;
};

type ReportCardProps = {
  report: ReportDescriptor;
  // undefined = all accessible partners (the partner params are omitted).
  partnersParam: string[] | undefined;
  disabled: boolean;
};

const ReportCard = ({ report, partnersParam, disabled }: ReportCardProps) => {
  const [fromDate, setFromDate] = useState<Date | null>(
    new Date(new Date().getFullYear(), 0, 1)
  );
  const [toDate, setToDate] = useState<Date | null>(new Date());
  const [isGenerating, setIsGenerating] = useState(false);
  const { addNotification, removeNotificationWithDelay } = useNotification();

  const handleDownloadClick = async () => {
    setIsGenerating(true);
    try {
      const blob = await downloadReportCsv(report.key, {
        ...(report.hasDates && fromDate
          ? { startDay: formatDate(fromDate) }
          : {}),
        ...(report.hasDates && toDate ? { endDay: formatDate(toDate) } : {}),
        partners: partnersParam,
      });
      downloadFile(blob, `${report.fileName}_${new Date().toISOString()}.csv`);
    } catch (error) {
      removeNotificationWithDelay(
        addNotification(
          `Could not generate ${report.title}: ${(error as Error).message}`,
          'alert'
        ),
        15000
      );
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Card>
      <h3>{report.title}</h3>
      {report.description}
      {report.hasDates && (
        <DatesWrapper>
          <NamedItem>
            <label>From date</label>
            <DatePicker
              className='default'
              locale='sv'
              dateFormat='yyyy-MM-dd'
              maxDate={new Date()}
              selected={fromDate}
              onChange={(date: Date | null) => setFromDate(date)}
            />
          </NamedItem>
          <NamedItem>
            <label>To date (inclusive)</label>
            <DatePicker
              className='default'
              locale='sv'
              dateFormat='yyyy-MM-dd'
              selected={toDate}
              minDate={fromDate ?? undefined}
              maxDate={new Date()}
              onChange={(date: Date | null) => setToDate(date)}
            />
          </NamedItem>
        </DatesWrapper>
      )}
      <ButtonWrapper>
        <Button
          theme='orange'
          size='small'
          onClick={handleDownloadClick}
          disabled={disabled || isGenerating}
        >
          <SpinnerWrapper isSpinning={isGenerating}>
            <Icon iconName='faArrowsRotate' />
          </SpinnerWrapper>{' '}
          {isGenerating ? 'Generating…' : 'Download'}
        </Button>
      </ButtonWrapper>
    </Card>
  );
};

export default ReportCard;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

export const SpinnerWrapper = styled.span<{ isSpinning: boolean }>`
  display: inline-block;
  animation: ${({ isSpinning }) =>
    isSpinning
      ? css`
          ${rotate} 2s linear infinite
        `
      : ''};
`;

export const Card = styled.div`
  background-color: #fff;
  border: 0.0625rem solid #dedede;
  padding: 0.625rem;
  border-radius: 0.625rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ButtonWrapper = styled.div`
  align-self: flex-end;
  margin-top: 0.625rem;
`;

const DatesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;

  input {
    border-color: #c8ccd0;
  }
`;
